import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, ShieldCheck, WifiOff, Key, BookOpen, Quote, Cpu } from "lucide-react";
import Mark, { IntroSplash } from "@/components/Mark";
import TrainPanel from "@/components/TrainPanel";
import { ExpertCard, SectionHead } from "@/components/Kit";
import { fetchAppDownload, fetchCatalog, fetchEngines, fetchTiers, trackDownloadClick } from "@/lib/api";
import { useTitle } from "@/lib/brand";

/**
 * HOME — rebuilt 2026-10-27 to the app's design (Dog: "the website should follow a lot of the
 * feel of the new app … 100% enterprise level … it should look like it belongs in 2028").
 * The old terminal canvas, the "[Awaiting Prompt]" caret and the watermark are gone; the
 * training bar lives on as the Train section (TrainPanel), with the same endpoints and rules.
 */
export default function LandingPage() {
  useTitle("");
  const [meta, setMeta] = useState(null);
  const [experts, setExperts] = useState([]);
  const [engines, setEngines] = useState([]);
  const [tiers, setTiers] = useState([]);

  useEffect(() => { fetchAppDownload().then(setMeta).catch(() => {}); }, []);
  useEffect(() => { fetchCatalog().then((d) => setExperts((d.experts || []).filter((e) => e.available))).catch(() => {}); }, []);
  useEffect(() => { fetchEngines().then((d) => setEngines(d?.engines || [])).catch(() => {}); }, []);
  useEffect(() => { fetchTiers().then((d) => setTiers((d?.tiers || []).filter((t) => t.available))).catch(() => {}); }, []);

  const apk = meta?.apk_url;
  const from = tiers.length ? tiers.reduce((a, b) => ((a.price_cents || 1e9) < (b.price_cents || 1e9) ? a : b)) : null;

  return (
    <main data-testid="home" className="ex-page">
      {/* the mark traces, spins and bursts into this page — the first thing anyone sees (Dog 2026-10-27) */}
      <IntroSplash />
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="ex-glow" style={{ width: 720, height: 720, left: "50%", top: -120, transform: "translateX(-50%)" }} />
        <div className="ex-wrap relative pt-16 md:pt-24 pb-24 md:pb-32 grid gap-14 lg:grid-cols-[1.15fr_1fr] items-center">
          <div>
            <div className="ex-chip ex-rise mb-8"><span className="ex-dot" /> Private AI for Android</div>
            {/* Dog's hero, dictated 2026-08-30 (SITE_ARCHITECTURE §4b): "Download your AI" — the AI is the
                thing, and it is the whole thing — then "The entire AI lives on your device. It's yours, and it's free." */}
            <h1 className="ex-display ex-rise-2">
              Download<br /><span className="ex-gradient-text">your AI.</span>
            </h1>
            <p className="ex-lede mt-7 max-w-xl ex-rise-3">
              The entire AI lives on your device. It's yours, and it's free. No account, no cloud — and once it's installed, it keeps working in airplane mode.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3 ex-rise-4">
              {apk ? (
                <a href={apk} download="Experts.apk" onClick={() => trackDownloadClick("apk")} className="ex-btn ex-btn-primary" data-testid="hero-download">
                  <Download className="h-4 w-4" strokeWidth={2} /> Download for Android
                </a>
              ) : (
                <Link to="/app" className="ex-btn ex-btn-primary" data-testid="hero-download">
                  <Download className="h-4 w-4" strokeWidth={2} /> Download for Android
                </Link>
              )}
              <Link to="/experts" className="ex-btn ex-btn-secondary">Explore free Experts</Link>
            </div>
            <div className="mt-5 ex-tiny ex-rise-4">
              Free{meta?.min_android ? ` · Android ${String(meta.min_android).replace(/^Android\s*/i, "")}` : ""}{meta?.apk_size_mb ? ` · ${meta.apk_size_mb} MB` : ""} · The Engine downloads once, inside the app
            </div>
          </div>
          {/* on a computer the mark rests here after the opening; on a phone the opening is the mark */}
          <div className="hidden lg:flex justify-center">
            <Mark size={400} strokeBoost={1.25} className="text-white opacity-90" />
          </div>
        </div>

        <div className="ex-wrap pb-6">
          <ul className="flex flex-wrap justify-center gap-x-8 gap-y-3 ex-small">
            {/* Dog's list (08-30, amended 10-26: "Free Experts to start") — every line kept */}
            {["No login", "No phone number", "No email", "No cloud", "No targeted marketing", "No metadata", "No monthly fees", "Free Experts to start", "100% yours after download"].map((t) => (
              <li key={t} className="inline-flex items-center gap-2"><span className="ex-dot opacity-60" /> {t}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── THREE PROMISES ───────────────────────────────────── */}
      <section className="ex-section">
        <div className="ex-wrap">
          <SectionHead eyebrow="Why Experts" title="Built on three promises." lede="The things most AI apps ask you to give up are the things Experts was designed around." />
          <div className="mt-14 grid gap-4 md:grid-cols-3">
            <Pillar icon={ShieldCheck} title="Private by design" body="Everything you type stays on your phone. There is no server on the other end of the conversation — nothing to log, sell or train on." />
            <Pillar icon={WifiOff} title="Works offline" body="The Engine downloads once. After that, Experts answers with the network switched off — on a plane, off the grid, anywhere." />
            <Pillar icon={Key} title="Yours to keep" body="No subscription and no sign-in. Every Expert is a file you own: save a copy, back it up, or move it to a new phone with its seven recovery words." />
          </div>
        </div>
      </section>

      {/* ── HOW IT THINKS ────────────────────────────────────── */}
      <section className="ex-section border-t border-[color:var(--ex-line)]">
        <div className="ex-wrap grid gap-14 lg:grid-cols-2 items-center">
          <div>
            <SectionHead
              eyebrow="How it works"
              title="One Engine. As many Experts as you need."
              lede="The Engine is the AI that does the thinking. An Expert is a specialist that sits on top of it — trained on one subject, carrying its own library of checked facts."
            />
            <ul className="mt-10 space-y-6">
              <Feature icon={BookOpen} title="Answers from its own library">Before an Expert answers, it searches its library and confirms that a fact really states the answer.</Feature>
              <Feature icon={Quote} title="Sources one tap away">Every answer shows the exact notes it used, and where each one came from.</Feature>
              <Feature icon={Cpu} title="Honest when it doesn't know">When the library doesn't cover a question, the Expert says so — then the Engine gives its own answer, clearly labelled.</Feature>
            </ul>
            <Link to="/how-it-works" className="ex-btn ex-btn-quiet -ml-4 mt-8">See the whole journey <ArrowRight className="h-4 w-4" strokeWidth={1.8} /></Link>
          </div>
          <div className="ex-panel p-8 md:p-10">
            <div className="ex-eyebrow mb-6">What happens when you ask</div>
            <ol className="space-y-0">
              {[
                ["Your question stays on your phone", "Nothing is sent anywhere. The Engine reads it on your own hardware."],
                ["The Expert searches its library", "Thousands of checked facts on one subject, stored on your device."],
                ["It confirms a fact really states the answer", "If none does, it says so — and the Engine answers on its own, clearly labelled."],
                ["You get the answer, with its sources", "Tap Sources to read the exact notes and where each one came from."],
              ].map(([h, b], i, all) => (
                <li key={h} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <span className="ex-dot mt-[7px]" />
                    {i < all.length - 1 && <span className="flex-1 w-px bg-[color:var(--ex-line)] my-2" />}
                  </div>
                  <div className="pb-7">
                    <div className="text-white text-[15px] font-medium">{h}</div>
                    <div className="ex-small mt-1">{b}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ── FREE EXPERTS ─────────────────────────────────────── */}
      <section className="ex-section border-t border-[color:var(--ex-line)]">
        <div className="ex-wrap">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow="Start free" title="Experts, ready to download." lede="Each one is tested on its Engine before it is published. Free, and yours to keep." />
            <Link to="/experts" className="ex-btn ex-btn-secondary ex-btn-sm">All Experts <ArrowRight className="h-4 w-4" strokeWidth={1.8} /></Link>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {experts.slice(0, 3).map((e) => <ExpertCard key={e.id} expert={e} />)}
            {experts.length === 0 && [0, 1, 2].map((i) => <div key={i} className="ex-card min-h-[236px] opacity-40" />)}
          </div>
        </div>
      </section>

      {/* ── TRAIN ────────────────────────────────────────────── */}
      <section id="train" className="ex-section border-t border-[color:var(--ex-line)] scroll-mt-20">
        <div className="ex-wrap grid gap-12 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <SectionHead
              eyebrow="Train your own"
              title="An Expert on anything you name."
              lede="Tell us the subject. We find the best sources, check every fact on its own, train the Expert for your phone's Engine and test it before it reaches you."
            />
            {from && (
              <div className="mt-8 flex items-baseline gap-3">
                <span className="ex-num text-[44px] text-white leading-none">{from.price_text}</span>
                <span className="ex-small">one-time · from {from.label}</span>
              </div>
            )}
            <ul className="mt-8 space-y-3 ex-small">
              <li>· One payment. No subscription.</li>
              <li>· The finished Expert is a file you own, with seven recovery words.</li>
              <li>· Training takes hours, not days. You'll see every stage as it happens.</li>
            </ul>
          </div>
          <div><TrainPanel /></div>
        </div>
      </section>

      {/* ── ENGINES ──────────────────────────────────────────── */}
      {engines.length > 0 && (
        <section className="ex-section border-t border-[color:var(--ex-line)]">
          <div className="ex-wrap">
            <SectionHead eyebrow="Engines" title="The largest Engine your phone can run." lede="The app reads your phone's memory and offers the Engine that fits. You can keep both and switch any time." />
            <div className="mt-12 grid gap-4 md:grid-cols-2">
              {engines.map((e) => (
                <div key={e.engine_id} className="ex-card p-7">
                  <div className="flex items-center justify-between">
                    <h3 className="ex-h3">{e.display}</h3>
                    {e.size_gb && <span className="ex-chip">{e.size_gb} GB</span>}
                  </div>
                  {e.blurb && <p className="ex-body mt-3">{e.blurb}</p>}
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {e.ram_tier_gb && <Stat k="Memory" v={`${e.ram_tier_gb} GB or more`} />}
                    {e.free_storage_gb && <Stat k="Free space" v={`${e.free_storage_gb} GB`} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CLOSE ────────────────────────────────────────────── */}
      <section className="ex-section border-t border-[color:var(--ex-line)]">
        <div className="ex-narrow text-center">
          <Mark size={56} strokeBoost={2} className="mx-auto text-white" />
          <h2 className="ex-h1 mt-8">Your AI. Your phone. Nobody else's business.</h2>
          <p className="ex-lede mt-5">Free to download. No account to make.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/app" className="ex-btn ex-btn-primary"><Download className="h-4 w-4" strokeWidth={2} /> Get the app</Link>
            <a href="#train" className="ex-btn ex-btn-secondary">Train an Expert</a>
          </div>
        </div>
      </section>
    </main>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="ex-card p-7">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--ex-user)]">
        <Icon className="h-5 w-5 text-white" strokeWidth={1.7} />
      </span>
      <h3 className="ex-h3 mt-6">{title}</h3>
      <p className="ex-body mt-3">{body}</p>
    </div>
  );
}

function Feature({ icon: Icon, title, children }) {
  return (
    <li className="flex gap-4">
      <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[color:var(--ex-raised)] border border-[color:var(--ex-line)]">
        <Icon className="h-[18px] w-[18px] text-white" strokeWidth={1.7} />
      </span>
      <span>
        <span className="block text-white font-medium">{title}</span>
        <span className="block ex-body mt-1">{children}</span>
      </span>
    </li>
  );
}

function Stat({ k, v }) {
  return (
    <div className="rounded-xl bg-[color:var(--ex-panel)] border border-[color:var(--ex-line)] px-4 py-3">
      <div className="ex-tiny">{k}</div>
      <div className="text-white text-[14px] mt-0.5">{v}</div>
    </div>
  );
}
